import { useCallback, useMemo, useState } from "react";
import useAllAssets from "domains/assets/hooks/useAllAssets";
import { FILTER_ASSET_TYPES } from "domains/file-manager/constants/AssetFilter";
import { useJobSessionContext } from "domains/jobs/contexts/JobSessionProviderV2";
import { useAssetsSearch } from "domains/search/hooks/useSearch";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import NewUserEmptyState from "domains/ui/components/NewUserEmptyState";
import { useUserContext } from "domains/user/contexts/UserProvider";
import {
  GetAssetsByAssetIdApiResponse,
  GetRecommendationsModelsApiResponse,
  useGetModelsByModelIdQuery,
  useGetRecommendationsModelsQuery,
} from "infra/api/generated/api";
import { useGetAssetsBulkQuery } from "infra/store/apiSlice";

import { skipToken } from "@reduxjs/toolkit/dist/query";

interface UseNewUserEmptyStateProps {
  modelId?: string;
  isTexture?: boolean;
}

type Model = GetRecommendationsModelsApiResponse["models"][0];
type Asset = GetAssetsByAssetIdApiResponse["asset"];
export type AssetWithModel = Asset & {
  model: Model;
};

export function useNewUserEmptyState({
  modelId,
  isTexture = false,
}: UseNewUserEmptyStateProps = {}) {
  const minExamples = 10;

  const { selectedProject } = useTeamContext();
  const { user } = useUserContext();
  const { featureFlags } = useUserContext();
  const hasNewUserEmptyStateFlag = featureFlags?.includes(
    "new-user-empty-state"
  );
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);
  const { jobs, files, isLoading } = useJobSessionContext();

  let skip: boolean =
    isLoading ||
    !hasNewUserEmptyStateFlag ||
    hasGeneratedOnce ||
    // Inferences founds in the session, no need to useSearch
    !!jobs?.length ||
    !!files?.length;

  const filteredTypes = useMemo(() => {
    const prefix = isTexture ? "texture:" : "image:inference";

    return (
      Object.entries(FILTER_ASSET_TYPES)
        .filter(([key]) => key.startsWith(prefix))
        .map(([_, type]) => type.value)
        .flat()
        // Uploaded image are not generations
        .filter((type) => type !== "uploaded")
    );
  }, [isTexture]);

  // Jobs and files above can be filtered, here we get the first page of all assets
  const { files: allAssets } = useAllAssets({
    authorId: user?.id!,
    types: filteredTypes,
  });
  // Inference found in allAssets, no need to useSearch
  skip = skip || !!allAssets?.length;

  // Get an image to see if user ever generated an image, if not show an empty state carousel
  const { files: searchedAssets, isLoading: isFirstImageLoading } =
    useAssetsSearch({
      isEmptyQueryAllowed: !skip,
      authorId: user?.id,
      types: filteredTypes,
      privacy: "private",
    });

  // Update skip status now that we fetched more info
  skip = !!searchedAssets[0] || isFirstImageLoading || skip;

  // If model is selected then get the model examples images
  const { currentData: modelData, isFetching: isModelDataFetching } =
    useGetModelsByModelIdQuery(
      modelId && !skip
        ? {
            projectId: selectedProject.id,
            modelId,
          }
        : skipToken
    );

  // Get model recommendations when no model is selected or when we need more examples
  const {
    currentData: modelRecommendations,
    isFetching: isModelRecommendationsFetching,
  } = useGetRecommendationsModelsQuery(
    !modelId ||
      ((modelData?.model.exampleAssetIds?.length ?? 0) < minExamples &&
        !skip &&
        !isModelDataFetching)
      ? {
          projectId: selectedProject.id,
          limit: "30",
          privacy: "public",
          tags: isTexture ? "sc:texture" : undefined,
        }
      : skipToken
  );

  const modelFilter = useCallback(
    (model: Model | undefined) => {
      if (!model?.tags) return true;
      return isTexture
        ? model.tags.includes("sc:texture")
        : !model.tags.includes("sc:texture");
    },
    [isTexture]
  );

  // Calculate how many more examples we need, filtering models based on texture tag
  const remainingExamples = useMemo(() => {
    const modelExampleAssets = modelData?.model;
    // Skip if model doesn't match texture filter
    if (modelFilter(modelData?.model)) {
      return minExamples;
    }
    return modelExampleAssets?.exampleAssetIds?.length
      ? Math.max(0, minExamples - modelExampleAssets.exampleAssetIds.length)
      : minExamples;
  }, [modelData?.model, minExamples, modelFilter]);

  // Get example assets from model recommendations, using multiple examples per model if needed
  const recommendationExamplesAssetsOnePerModel = useMemo(() => {
    let remainingExamplesToGetFromRecommendations =
      remainingExamples - (modelData?.model.exampleAssetIds?.length ?? 0);
    const filteredModels =
      modelRecommendations?.models?.filter(modelFilter) ?? [];
    const result: string[] = [];

    // Get multiple examples per model until we have enough, this happen when there is not enough recommended models
    let examplesPerModel = 1;

    while (
      remainingExamplesToGetFromRecommendations > 0 &&
      examplesPerModel <= 10
    ) {
      for (const model of filteredModels) {
        if (remainingExamplesToGetFromRecommendations <= 0) break;

        if (
          model.exampleAssetIds?.length &&
          model.exampleAssetIds.length >= examplesPerModel
        ) {
          result.push(model.exampleAssetIds[examplesPerModel - 1]);
          remainingExamplesToGetFromRecommendations--;
        }
      }
      examplesPerModel++;
    }

    return result;
  }, [
    modelData?.model.exampleAssetIds?.length,
    modelRecommendations?.models,
    remainingExamples,
    modelFilter,
  ]);

  const { currentData: modelExamples, isFetching: isModelExamplesFetching } =
    useGetAssetsBulkQuery(
      skip
        ? skipToken
        : {
            projectId: selectedProject.id,
            body: {
              assetIds: [
                ...(modelData?.model.exampleAssetIds ?? []),
                ...(recommendationExamplesAssetsOnePerModel ?? []),
              ],
            },
          }
    );

  const emptyStateAssets = useMemo(
    () => (skip ? undefined : modelExamples?.assets),
    [modelExamples?.assets, skip]
  );

  const newUserEmptyStateAssets = useMemo(
    () =>
      skip
        ? undefined
        : emptyStateAssets?.map((asset): AssetWithModel => {
            // Find the matching model for this asset
            const assetModel = modelRecommendations?.models?.find(
              (model) => model.id === asset.metadata?.modelId
            );

            const tags = asset?.tags?.length
              ? asset.tags
              : assetModel?.tags?.length
              ? assetModel.tags
              : modelData?.model?.tags?.length
              ? modelData.model.tags
              : [];

            const assetWithModel: AssetWithModel = {
              ...asset,
              tags,
              model: assetModel || modelData?.model || ({} as Model),
            };

            return assetWithModel;
          }),
    [skip, emptyStateAssets, modelRecommendations?.models, modelData?.model]
  );

  const NewUserEmptyStateComponent = useMemo(
    () =>
      newUserEmptyStateAssets
        ? () => <NewUserEmptyState assets={newUserEmptyStateAssets ?? []} />
        : undefined,
    [newUserEmptyStateAssets]
  );

  const fmHeaderStyleProps = useMemo(() => {
    return NewUserEmptyStateComponent && !skip
      ? ({
          pointerEvents: "none",
          // Dim all content inside header, but not the background as it can be scrolled over the empty state in mobile view
          sx: {
            "& > *": {
              opacity: 0.5,
            },
          },
        } as const)
      : {};
  }, [NewUserEmptyStateComponent, skip]);

  return {
    fmHeaderStyleProps,
    NewUserEmptyStateComponent,
    setHasGeneratedOnce,
    isLoading:
      isFirstImageLoading ||
      isModelDataFetching ||
      isModelRecommendationsFetching ||
      isModelExamplesFetching,
  };
}
